// Column-mapping screen (Phase 3, Piece B — spec §9, cloud).
//
// Shown when a tabular CSV isn't claimed by a known adapter. The user confirms
// which column plays each role via field-first rows — a plain-language question
// on the left, a column dropdown on the right, top-aligned side by side. Once
// the required roles are set, a compact From / When / Message preview of the
// first messages shows how Aurora will read the file (the "looks good" step).
//
// Flat cloud-wizard chrome: a step title + lede, the only bordered elements are
// the form fields (the per-role <select>s) and divider rules between rows. NO
// tech/domain jargon — we ask "Who sent it?", never "map the body field".

import { useCallback, useEffect, useRef, useState } from "react";

import {
  REQUIRED_ROLES,
  isComplete,
  projectRows,
  type ColumnMapping,
  type Role,
} from "../adapters/_column_mapping";
import { buildIntakeMailto } from "../lib/intake";

// Plain-language question per role (no jargon). Required roles must be chosen;
// optional roles may be left as "Not in this file".
const ROLE_QUESTION: Record<Role, string> = {
  sender: "Who sent it?",
  timestamp: "When was it sent?",
  body: "The message",
  recipient: "Who received it?",
  subject: "Subject line",
};
// Sub-hint for optional roles only (required roles are self-evident).
const ROLE_OPT_HINT: Partial<Record<Role, string>> = {
  recipient: "Leave blank for group threads.",
  subject: "Many exports don't have one.",
};

// Display order leads with the message itself, then who/when, then the optional
// roles — the order Rich approved in the mockup (not the internal role order).
const DISPLAY_ORDER: Role[] = [
  "body",
  "sender",
  "timestamp",
  "recipient",
  "subject",
];

const NOT_PRESENT = "__none__";

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + "…" : s;
}

export interface ColumnMappingStepProps {
  filename: string;
  /** The file's detected headers. */
  headers: string[];
  /** A few raw row-dicts for the preview (header → value). */
  sampleRows: Record<string, string>[];
  /** Current { role: header | null } mapping (controlled). */
  mapping: ColumnMapping;
  /** Aurora's original fuzzy-matched guess, for "reset to suggested". */
  suggested?: ColumnMapping;
  /** Total data rows in the file, for the "N messages ready" confirmation. */
  rowCount?: number;
  /**
   * Pilot code the user logged in with — embedded (visible) in the "can't match
   * these up" get-in-touch email so the note ties to their case. Optional.
   */
  accessCode?: string;
  onChange: (mapping: ColumnMapping) => void;
  onContinue: () => void;
  onBack: () => void;
}

export function ColumnMappingStep(props: ColumnMappingStepProps) {
  const { filename, headers, sampleRows, mapping, suggested, rowCount } = props;

  const complete = isComplete(mapping);
  // Quiet escape: matching columns is usually doable, but a file that isn't
  // really message data (or whose columns can't be expressed here — e.g. a
  // split date/time) leaves the user stuck. Same honest handoff as the recover
  // screen, kept understated since most users won't need it.
  const helpMailto = buildIntakeMailto({
    subject: "Aurora — help matching my columns",
    lead:
      "Hi,\n\nI have a spreadsheet (CSV) of my messages, but Aurora couldn't " +
      "match up the columns. Could you help me get them analyzed?",
    code: props.accessCode,
  });
  // Aurora pre-fills the dropdowns with a fuzzy-matched guess; once the user
  // has changed any of them, offer a one-click revert to that suggestion.
  const changedFromSuggested =
    suggested != null &&
    DISPLAY_ORDER.some((r) => (mapping[r] ?? null) !== (suggested[r] ?? null));
  // First few messages projected through the mapping — the side-by-side preview
  // shown once the required roles are set. The optional To/Subject columns
  // appear only when those roles are mapped, so the user can SEE the effect of
  // mapping them (and they don't take table room when unused).
  const preview = complete ? projectRows(sampleRows, mapping, 3) : [];
  const showTo = Boolean(mapping.recipient);
  const showSubject = Boolean(mapping.subject);

  // Right-edge fade: shown only while the preview table can still scroll right
  // (i.e. there's a column hidden past the edge), so the cut-off message reads
  // as "scroll for more" rather than broken. Recomputed on scroll, on resize,
  // and whenever the column set changes the table's width.
  const previewWrapRef = useRef<HTMLDivElement>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const updateScrollFade = useCallback(() => {
    const el = previewWrapRef.current;
    if (!el) return;
    setCanScrollRight(el.scrollWidth - el.scrollLeft - el.clientWidth > 1);
  }, []);
  useEffect(() => {
    const el = previewWrapRef.current;
    if (!el) {
      setCanScrollRight(false);
      return;
    }
    updateScrollFade();
    el.addEventListener("scroll", updateScrollFade, { passive: true });
    window.addEventListener("resize", updateScrollFade);
    return () => {
      el.removeEventListener("scroll", updateScrollFade);
      window.removeEventListener("resize", updateScrollFade);
    };
  }, [updateScrollFade, complete, showTo, showSubject, preview.length]);

  function setRole(role: Role, value: string) {
    props.onChange({ ...mapping, [role]: value === NOT_PRESENT ? null : value });
  }

  return (
    <div className="col-map">
      <h1 className="step-title">Match up the columns.</h1>
      <p className="step-lede col-map-lede">
        <span className="col-map-lede-pre">Aurora didn't recognize&nbsp;</span>
        <code className="col-map-file-inline" title={filename}>
          {filename}
        </code>
        <span className="col-map-lede-post">
          &nbsp;— tell it which column is which.
        </span>
      </p>

      {changedFromSuggested && (
        <div className="col-map-resetbar">
          <button
            type="button"
            className="col-map-reset"
            onClick={() => suggested && props.onChange(suggested)}
          >
            ↺ Reset to Aurora's suggestions
          </button>
        </div>
      )}

      <div className="col-map-rows">
        {DISPLAY_ORDER.map((role) => {
          const required = (REQUIRED_ROLES as readonly Role[]).includes(role);
          return (
            <div className="col-map-row" key={role}>
              <div className="col-map-row-q">
                <label htmlFor={`role-${role}`} className="col-map-row-label">
                  {ROLE_QUESTION[role]}
                </label>
                {required ? (
                  <span className="col-map-required"> · required</span>
                ) : (
                  <span className="col-map-optional"> · optional</span>
                )}
              </div>

              <select
                id={`role-${role}`}
                className="col-map-select"
                value={mapping[role] ?? NOT_PRESENT}
                onChange={(e) => setRole(role, e.target.value)}
              >
                {required ? (
                  <option value={NOT_PRESENT}>Choose a column…</option>
                ) : (
                  <option value={NOT_PRESENT}>Not in this file</option>
                )}
                {headers.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>

              {!required && ROLE_OPT_HINT[role] && (
                <span className="col-map-row-hint">{ROLE_OPT_HINT[role]}</span>
              )}
            </div>
          );
        })}
      </div>

      {complete ? (
        <div className="col-map-confirm-ok">
          <p className="col-map-confirm-headline">
            Here's how Aurora reads the first few
            {typeof rowCount === "number" ? (
              <>
                {" "}
                of your <strong>{rowCount.toLocaleString()} messages</strong>
              </>
            ) : (
              <> messages</>
            )}{" "}
            — check they look right before continuing:
          </p>
          <div
            className={`col-map-preview-scroll${
              canScrollRight ? " is-scrollable" : ""
            }`}
          >
          <div className="col-map-preview-wrap" ref={previewWrapRef}>
          <table className="col-map-preview">
            <thead>
              <tr>
                <th>From</th>
                {showTo && <th>To</th>}
                <th>When</th>
                {showSubject && <th>Subject</th>}
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {preview.map((r, i) => (
                <tr key={i}>
                  <td className="col-map-preview-name">{r.sender || "—"}</td>
                  {showTo && (
                    <td className="col-map-preview-name">
                      {r.recipient || "—"}
                    </td>
                  )}
                  <td className="col-map-preview-when">{r.timestamp || "—"}</td>
                  {showSubject && (
                    <td className="col-map-preview-subj">
                      {r.subject ? truncate(r.subject, 28) : "—"}
                    </td>
                  )}
                  <td className="col-map-preview-msg">
                    {r.body ? truncate(r.body, 70) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          </div>
        </div>
      ) : (
        <p className="col-map-confirm-todo">
          Choose the message, who sent it, and when — then continue.
        </p>
      )}

      <p className="col-map-escape">
        Columns not matching up? Your file may not be message data —{" "}
        <a className="col-map-escape-link" href={helpMailto}>
          tell us what you have →
        </a>
      </p>

      <div className="wiz-cta">
        <button type="button" className="ghost" onClick={props.onBack}>
          ← Choose a different file
        </button>
        <button
          type="button"
          className="primary"
          disabled={!complete}
          onClick={props.onContinue}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}
